export type BillingCycle = 'monthly' | 'yearly'
export type CompliassistPlanId = 'simple' | 'pilot' | 'master'

/** Ordered feature list (excluding the always-shown "users" line, whose label
    is per-plan and lives in i18n at plans.{id}.users). */
export const FEATURE_KEYS = [
  'registre',
  'verifications',
  'levees',
  'scorerisk',
  'ia',
  'relances',
  'conseiller',
  'rapport',
  'portefeuille',
  'prestataires',
] as const
export type FeatureKey = (typeof FEATURE_KEYS)[number]

export interface CompliassistPlan {
  id: CompliassistPlanId
  popular?: boolean
  /** Prices in cents HT, per displayed month (yearly = monthly-equivalent).
      Filled at runtime from Stripe by the pricing server function. */
  priceHtCents: Record<BillingCycle, number>
  /** Stripe Price ID per cycle, from the DB (source of truth for checkout). */
  stripePriceId: Record<BillingCycle, string>
  /** Feature keys unlocked at this tier (subset of FEATURE_KEYS). */
  unlocked: FeatureKey[]
}

const SIMPLE_FEATURES: FeatureKey[] = [
  'registre', 'verifications', 'levees', 'scorerisk', 'ia',
]
const PILOT_FEATURES: FeatureKey[] = [...SIMPLE_FEATURES, 'relances', 'conseiller']

/** Static, code-side plan config (not stored in DB): unlocked features and the
    "popular" flag, keyed by plan id. Prices/price-ids come from Stripe/DB and
    are merged in by the pricing server function. Also fixes display order. */
export const PLAN_CONFIG: Record<
  CompliassistPlanId,
  { popular?: boolean; unlocked: FeatureKey[] }
> = {
  simple: { unlocked: SIMPLE_FEATURES },
  pilot: { popular: true, unlocked: PILOT_FEATURES },
  master: { unlocked: [...FEATURE_KEYS] },
}

/** Display/checkout order. */
export const PLAN_ORDER: CompliassistPlanId[] = ['simple', 'pilot', 'master']

/** Fixed-per-tier mapping: 1→simple, 2-3→pilot, 4-5→master. */
export function planForSites(sites: number): CompliassistPlanId {
  if (sites <= 1) return 'simple'
  if (sites <= 3) return 'pilot'
  return 'master'
}

/** Formats cents-HT to a whole-euro currency string, e.g. "49 €". */
export function formatPriceHt(cents: number, locale: string): string {
  return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}
